import React, { useState, forwardRef } from 'react';
import AgentMark from './AgentMark.jsx';
import AskBlock from './AskBlock.jsx';
import CounterCard from './CounterCard.jsx';
import ReferenceTile from './ReferenceTile.jsx';

const AgentTurn = forwardRef(function AgentTurn({ turn, threadAgent, onNav, focusComposer, onAnswer }, askRef) {
  const structured = !!(
    turn.ask
    || turn.summary
    || (turn.counters && turn.counters.length)
    || (turn.references && turn.references.length)
    || (turn.recommendations && turn.recommendations.length)
  );
  const hasBody = !!turn.body && (Array.isArray(turn.body) ? turn.body.length > 0 : true);
  const hasReasoning = !!(
    hasBody
    || (turn.counters && turn.counters.length)
    || (turn.references && turn.references.length)
    || (turn.recommendations && turn.recommendations.length)
  );
  const [open, setOpen] = useState(!structured || !hasReasoning); // legacy turns expand the body immediately

  return (
    <article className={`turn-agent ${turn.awaiting ? 'is-awaiting' : ''} ${structured ? 'is-structured' : ''}`}>
      {/* meta row (unchanged from before) */}
      <div className="meta">
        <span className="agent-info">
          <AgentMark agent={threadAgent} size="xs" />
          <span className="name">{threadAgent?.name?.toUpperCase()}</span>
        </span>
        <span className="sep">·</span>
        <span>{turn.date?.toUpperCase()}</span>
        {turn.dossierId && (
          <>
            <span className="sep">·</span>
            <span>DOSSIER ATTACHED</span>
          </>
        )}
        {turn.awaiting && (
          <span className="tag attention" style={{ marginLeft: 'auto' }}>
            <span className="pip" />Awaiting your read
          </span>
        )}
      </div>

      {turn.title && <h2 className="title">{turn.title}</h2>}

      {turn.ask && (
        <AskBlock
          ref={askRef}
          ask={turn.ask}
          onRespond={focusComposer}
          onAnswer={onAnswer}
        />
      )}

      {turn.summary && <p className="turn-summary">{turn.summary}</p>}

      {structured && hasReasoning && (
        <button type="button" className="reasoning-toggle" onClick={() => setOpen(v => !v)}>
          <span className="toggle-chev">{open ? '−' : '+'}</span>
          {open ? 'Hide the reasoning' : 'Show the reasoning'}
          {turn.counters && turn.counters.length > 0 && (
            <span className="toggle-meta">{turn.counters.length} counters</span>
          )}
          {turn.references && turn.references.length > 0 && (
            <span className="toggle-meta">{turn.references.length} references</span>
          )}
          {turn.recommendations && turn.recommendations.length > 0 && (
            <span className="toggle-meta">{turn.recommendations.length} recommendations</span>
          )}
        </button>
      )}

      {open && hasReasoning && (
        <div className="reasoning">
          {/* attachment rendering: TODO if/when added */}

          {/* body paragraphs — defensive against legacy turns where body might be a bare string */}
          {hasBody && (
            <div className="body">
              {(Array.isArray(turn.body) ? turn.body : [turn.body]).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}

          {turn.counters && turn.counters.length > 0 && (
            <div className="counters">
              <h3 className="reasoning-h">Counters</h3>
              {turn.counters.map((c, i) => <CounterCard key={i} counter={c} />)}
            </div>
          )}

          {turn.references && turn.references.length > 0 && (
            <div className="refs">
              <h3 className="reasoning-h">References pulled by {threadAgent?.name}</h3>
              <div className="refs-grid">
                {turn.references.map((r, i) => <ReferenceTile key={i} reference={r} />)}
              </div>
            </div>
          )}

          {turn.recommendations && turn.recommendations.length > 0 && (
            <div className="recs">
              <h3 className="reasoning-h">What {threadAgent?.name} recommends</h3>
              <ol className="recs-list">
                {turn.recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* footer actions: dossier button always shown when present; respond button only for legacy turns */}
      {(turn.dossierId || (!structured && turn.awaiting)) && (
        <div className="actions">
          {turn.dossierId && (
            <button className="btn small ghost" onClick={() => onNav(`/dossier/${turn.dossierId}`)}>
              Open the dossier
            </button>
          )}
          {!structured && turn.awaiting && (
            <button className="btn small accent" onClick={focusComposer}>
              Respond
            </button>
          )}
        </div>
      )}
    </article>
  );
});

export default AgentTurn;
